"use client";

import { useId, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { motion } from "framer-motion";

import { RecentSearchesList } from "@/components/layout/sidebar/recent-searches";
import {
  SidebarItem,
  useBottomScrollFade,
} from "@/components/layout/sidebar/sidebar-layout";
import { ScrollArea } from "@/components/ui/scroll-area";
import { TALENT_SEARCH_ROOT, matchSearchRoute } from "@/components/search/search-tabs";
import { Icon } from "@/components/ui/icon";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

type SectionTab = "searches" | "agents";

const TABS: {
  id: SectionTab;
  label: string;
  /** Locked tabs render but can't be selected. */
  locked?: boolean;
  lockedReason?: string;
}[] = [
  { id: "searches", label: "Searches" },
  {
    id: "agents",
    label: "Agents",
    locked: true,
    lockedReason: "Agents are coming soon",
  },
];

/* ── Segmented control ────────────────────────────────────────────────── */

function SectionTabs({
  value,
  onChange,
}: {
  value: SectionTab;
  onChange: (tab: SectionTab) => void;
}) {
  const layoutId = useId();

  return (
    /* Same chrome as the filter panel's Filters / Criteria switcher
       (`FilterPanelPaneSwitcher`), minus icons and counts. */
    <div
      role="tablist"
      aria-label="Sidebar sections"
      className={cn(
        "flex h-8 w-full min-w-0 shrink-0 items-center gap-0.5 rounded-lg p-0.5",
        "border border-border bg-contrast/3",
      )}
    >
      {TABS.map((tab) => {
        const selected = value === tab.id;
        const button = (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-disabled={tab.locked || undefined}
            tabIndex={selected ? 0 : -1}
            onClick={() => {
              if (!tab.locked) onChange(tab.id);
            }}
            className={cn(
              "relative flex h-full min-w-0 flex-1 items-center justify-center gap-1.5 rounded-[6px] px-2",
              "transition-colors duration-150 outline-none",
              "focus-visible:ring-2 focus-visible:ring-ai/40",
              tab.locked
                ? "cursor-not-allowed"
                : selected
                  ? "cursor-default"
                  : "cursor-pointer hover:bg-contrast/[0.04]",
            )}
          >
            {selected && (
              <motion.span
                layoutId={layoutId}
                aria-hidden
                className="absolute inset-0 rounded-[6px] bg-contrast/8 dark:bg-black/30"
                transition={{ type: "spring", stiffness: 500, damping: 30, mass: 0.5 }}
              />
            )}
            <span
              className={cn(
                "relative z-10 truncate text-[13px] font-medium transition-colors",
                tab.locked
                  ? "text-contrast/40"
                  : selected
                    ? "text-heading"
                    : "text-contrast/65",
              )}
            >
              {tab.label}
            </span>
            {tab.locked && (
              <Icon
                name="IconLock"
                size={12}
                aria-hidden
                className="relative z-10 shrink-0 text-heading opacity-40"
              />
            )}
          </button>
        );

        if (!tab.locked) return button;

        return (
          <Tooltip key={tab.id}>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent side="top" sideOffset={6}>
              {tab.lockedReason}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

/* ── "New talent search" row ──────────────────────────────────────────── */

function NewSearchRow({
  active,
  disabled,
  disabledReason,
}: {
  active: boolean;
  disabled: boolean;
  disabledReason: string;
}) {
  const content = (
    <>
      <span
        aria-hidden
        className={cn(
          "flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-dashed transition-colors duration-150",
          disabled
            ? "border-contrast/20"
            : active
              ? "border-ai bg-ai/12"
              : "border-contrast/30 group-hover/new:border-ai group-hover/new:bg-ai/10",
        )}
      >
        <Icon
          name="IconPlusSmall"
          size={12}
          className={cn(
            "transition-colors duration-150",
            disabled
              ? "text-contrast/25"
              : active
                ? "text-ai"
                : "text-contrast/50 group-hover/new:text-ai",
          )}
        />
      </span>
      <span className="truncate text-[13px] font-medium leading-5">
        New talent search
      </span>
    </>
  );

  const rowClass = cn(
    "group/new flex h-8 w-full items-center gap-2 rounded-md px-2 transition-colors duration-150",
    "outline-none focus-visible:ring-2 focus-visible:ring-ai/40",
  );

  if (disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            aria-disabled
            className={cn(rowClass, "cursor-not-allowed select-none text-contrast/35")}
          >
            {content}
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" align="center" className="max-w-xs">
          {disabledReason}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Link
      href={TALENT_SEARCH_ROOT}
      aria-current={active ? "page" : undefined}
      className={cn(
        rowClass,
        active
          ? "sidebar-active text-heading"
          : "text-contrast/65 hover:bg-contrast/4 hover:text-heading",
      )}
    >
      {content}
    </Link>
  );
}

/* ── Section ──────────────────────────────────────────────────────────── */

/**
 * The tabbed lower half of the sidebar. "Searches" is live (new search row,
 * recent list, browse-all); "Agents" is rendered locked until the feature
 * ships. On the icon rail the whole section folds into one Talent Search
 * icon.
 */
export function SidebarSearchesSection({
  expanded,
  disabled,
  disabledReason,
  fill = false,
}: {
  expanded: boolean;
  disabled: boolean;
  disabledReason: string;
  /**
   * Claim the sidebar's remaining height and scroll the list inside it, so
   * the nav above stays put. Off in the mobile drawer, where the whole panel
   * scrolls as one and a nested scroller would fight the touch gesture.
   */
  fill?: boolean;
}) {
  const pathname = usePathname();
  const [tab, setTab] = useState<SectionTab>("searches");
  const { viewportRef, fadeClassName } = useBottomScrollFade();

  const onSearchRoute = !!matchSearchRoute(pathname);
  const onSearchRoot = pathname === TALENT_SEARCH_ROOT;

  if (!expanded) {
    return (
      <div className="flex flex-col items-center px-3.5 py-2.5">
        <SidebarItem
          href={TALENT_SEARCH_ROOT}
          icon="IconSearchIntelligence"
          label="Talent Search"
          expanded={false}
          active={onSearchRoute}
          disabled={disabled}
          disabledReason={disabledReason}
        />
      </div>
    );
  }

  return (
    <section
      aria-label="Searches"
      data-slot="sidebar-searches"
      className={cn(
        "flex flex-col gap-2 px-3.5 py-3",
        fill && "min-h-0 flex-1",
      )}
    >
      <SectionTabs value={tab} onChange={setTab} />

      {tab === "searches" && (
        <div className={cn("flex flex-col gap-px", fill && "min-h-0 flex-1")}>
          {/* The new-search action stays put; only the history scrolls. */}
          <NewSearchRow
            active={onSearchRoot}
            disabled={disabled}
            disabledReason={disabledReason}
          />
          {fill ? (
            <ScrollArea
              className="min-h-0 flex-1"
              viewportRef={viewportRef}
              viewportClassName={cn("flex flex-col", fadeClassName)}
              contentClassName="flex w-full min-w-0 flex-col gap-px"
              constrainContentWidth
            >
              {/* "Browse all searches" lives at the tail of this list. */}
              <RecentSearchesList expanded />
            </ScrollArea>
          ) : (
            <RecentSearchesList expanded />
          )}
        </div>
      )}
    </section>
  );
}
